'use client';

import { useMemo } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha, Box, Typography } from '@mui/material';

import { EventMarket, EventOutcome, EventResponse } from '@/types';

interface ResolvedOutcomeRowProps {
  event: EventResponse;
  market: EventMarket;
  isSelected?: boolean;
  onSelect?: () => void;
}

export function ResolvedOutcomeRow({
  event,
  market,
  isSelected,
  onSelect,
}: ResolvedOutcomeRowProps) {
  const winner = useMemo(() => {
    let outcomesArray: EventOutcome[] = [];
    outcomesArray = market.outcomes as EventOutcome[];
    return outcomesArray.find((o) => String(o.price) === '1');
  }, [market]);

  if (!winner) return null;

  const winnerName = (winner as any).displayName || winner.name || 'Unknown';
  const isNo = winnerName.toLowerCase() === 'no';
  const color = isNo ? 'error.main' : 'success.main';
  const Icon = isNo ? CancelIcon : CheckCircleIcon;

  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2,
        px: 2.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: onSelect ? 'pointer' : 'default',
        bgcolor: (theme) => {
          if (isSelected) {
            return theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.15)
              : alpha(theme.palette.primary.main, 0.08);
          }
          return 'transparent';
        },
        position: 'relative',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isSelected ? 'translateY(-1px)' : 'none',
        boxShadow: isSelected
          ? (theme) =>
              theme.palette.mode === 'dark'
                ? `0 4px 20px -5px ${alpha(theme.palette.common.black, 0.5)}`
                : `0 4px 15px -5px ${alpha(theme.palette.primary.main, 0.15)}`
          : 'none',
        zIndex: isSelected ? 2 : 1,
        '&::before': isSelected
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '6px',
              bgcolor: 'primary.main',
              boxShadow: (theme) => `2px 0 10px ${alpha(theme.palette.primary.main, 0.4)}`,
            }
          : undefined,
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': onSelect
          ? {
              bgcolor: (theme) => {
                if (isSelected) {
                  return theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.2)
                    : alpha(theme.palette.primary.main, 0.12);
                }
                return theme.palette.mode === 'dark'
                  ? alpha(theme.palette.text.primary, 0.05)
                  : alpha(theme.palette.text.primary, 0.03);
              },
              transform: isSelected ? 'translateY(-2px)' : 'none',
              boxShadow: isSelected
                ? (theme) =>
                    theme.palette.mode === 'dark'
                      ? `0 6px 25px -5px ${alpha(theme.palette.common.black, 0.6)}`
                      : `0 6px 20px -5px ${alpha(theme.palette.primary.main, 0.2)}`
                : 'none',
            }
          : {},
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          fontSize: '1rem',
        }}
      >
        {market.groupItemTitle || market.question || 'Market resolved'}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderRadius: 1.5,
          bgcolor: (theme) =>
            alpha(isNo ? theme.palette.error.main : theme.palette.success.main, 0.1),
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: color,
            fontSize: '0.875rem',
          }}
        >
          {winnerName}
        </Typography>
        <Icon sx={{ color, fontSize: 18 }} />
      </Box>
    </Box>
  );
}
